import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Settings as SettingsIcon, Sun, Moon, Palette, User, Bell, Shield, Info, ChevronDown, AlertTriangle, X, Users, MapPin, Calendar, Lock, Eye, EyeOff, FileText, Download, Copy, Heart, Sparkles, Phone, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { TERMS_AND_CONDITIONS, PRIVACY_POLICY, COOKIES_POLICY, downloadPolicy, copyToClipboard } from '../data/policies';

// About eternitysingles content
const ABOUT_ETERNITYSINGLES = {
  title: "About eternitysingles®",
  tagline: "Connecting Hearts, Nurturing Minds",
  intro: "eternitysingles® is a revolutionary dating platform designed for individuals who prioritize mental health and emotional wellbeing in their search for meaningful connections.",
  sections: [
    {
      title: "Our Mission",
      content: "We believe that true love flourishes when both partners are mentally and emotionally healthy. Our platform creates a safe, supportive environment where singles can connect based on shared values around mental wellness, personal growth, and authentic relationships."
    },
    {
      title: "What Makes Us Different",
      items: [
        { label: "Mental Health Focus", desc: "Unlike traditional dating apps, we integrate wellness tools, mood tracking, and support resources directly into the dating experience." },
        { label: "Support Groups", desc: "Connect with others who share similar experiences through our community support groups for anxiety, depression, relationships, and more." },
        { label: "Wellness Resources", desc: "Access guided meditations, breathing exercises, journaling prompts, and self-care activities to support your mental health journey." },
        { label: "Safe Space", desc: "We prioritize creating a judgment-free environment where you can be your authentic self." },
        { label: "Meaningful Matches", desc: "Our matching considers not just interests, but mental health goals, therapy experience, and openness levels." }
      ]
    },
    {
      title: "Our Values",
      values: [
        { emoji: "🌱", label: "Growth", desc: "We encourage personal development and self-improvement." },
        { emoji: "💚", label: "Compassion", desc: "We foster understanding and empathy in all interactions." },
        { emoji: "🔒", label: "Privacy", desc: "Your mental health journey is personal, and we protect it." },
        { emoji: "🤝", label: "Community", desc: "We believe in the power of support and connection." }
      ]
    },
    {
      title: "Who We're For",
      content: "eternitysingles® welcomes anyone 18+ who values mental wellness and seeks genuine connections. Whether you're managing anxiety, recovering from past relationships, or simply prioritising your emotional health, you belong here."
    }
  ],
  footer: "© 2026 eternitysingles®. All rights reserved. Developer Tyrus."
};

const Settings = ({ user }) => {
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  
  // Notification settings state
  const [matchNotifications, setMatchNotifications] = useState(true);
  const [messageNotifications, setMessageNotifications] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [pushNotifications, setPushNotifications] = useState(true);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Mobile number edit state
  const [mobileNumber, setMobileNumber] = useState(user?.mobile_number || '');
  const [savedMobileNumber, setSavedMobileNumber] = useState(user?.mobile_number || '');
  const [isEditingMobile, setIsEditingMobile] = useState(false);
  const [isSavingMobile, setIsSavingMobile] = useState(false);

  // About modal state
  const [showAboutModal, setShowAboutModal] = useState(false);

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    appearance: true,
    account: false,
    security: false,
    notifications: false,
    privacy: false,
    genderPreferences: false,
    legal: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Load user preferences on component mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        const response = await axios.get('/users/me/preferences');
        const preferences = response.data;
        
        // Update notification settings if they exist
        if (preferences.notifications) {
          setMatchNotifications(preferences.notifications.matches ?? true);
          setMessageNotifications(preferences.notifications.messages ?? true);
          setEmailNotifications(preferences.notifications.email ?? false);
          setPushNotifications(preferences.notifications.push ?? true);
        }
        
        // Update privacy settings if they exist
        if (preferences.privacy) {
          setProfileVisibility(preferences.privacy.profile_visibility ?? 'Public');
        }
        
        // Update visibility settings if they exist
        if (preferences.visibility_settings) {
          setProfileVisibilitySettings(preferences.visibility_settings);
        }
        
        // Update gender preference if it exists
        if (preferences.gender_preference) {
          setGenderPreference(preferences.gender_preference);
        }
      } catch (error) {
        console.error('Error loading user preferences:', error);
        // Don't show error toast as this is not critical - use defaults
      }
    };

    loadUserPreferences();
  }, []);
  
  // Privacy settings state  
  const [profileVisibility, setProfileVisibility] = useState('Public');
  const [showPrivacyDropdown, setShowPrivacyDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Profile visibility preferences - who can see your profile
  const [profileVisibilitySettings, setProfileVisibilitySettings] = useState({
    hideFromMen: false,
    hideFromWomen: false
  });
  
  // Gender preference dropdown
  const [genderPreference, setGenderPreference] = useState('Men Looking for Women');
  const [showGenderDropdown, setShowGenderDropdown] = useState(false);

  const handleDownloadData = async () => {
    try {
      toast.info('Preparing your data for download...');
      
      // Get user data from backend
      const response = await axios.get('/users/me');
      const userData = response.data;
      
      // Create downloadable data object
      const dataToDownload = {
        profile: {
          email: userData.email,
          username: userData.username,
          full_name: userData.full_name,
          age: userData.age,
          location: userData.location,
          bio: userData.bio,
          interests: userData.interests_hobbies || [],
          mental_health_goals: userData.mental_health_goals || [],
          relationship_status: userData.relationship_status,
          looking_for: userData.looking_for,
          created_at: userData.created_at
        },
        settings: {
          notifications: {
            matches: matchNotifications,
            messages: messageNotifications,
            email: emailNotifications,
            push: pushNotifications
          },
          privacy: {
            profile_visibility: profileVisibility
          },
          theme: theme
        },
        export_date: new Date().toISOString(),
        app: 'eternitysingles'
      };
      
      // Create and download file
      const dataStr = JSON.stringify(dataToDownload, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `eternitysingles-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Your data has been downloaded successfully!');
    } catch (error) {
      console.error('Error downloading data:', error);
      toast.error('Failed to download data. Please try again.');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      await axios.put('/users/me/password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Password changed successfully! 🔒');
      
      // Clear the form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.status === 401) {
        toast.error('Current password is incorrect');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to change password. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete my account') {
      toast.error('Please type "delete my account" to confirm');
      return;
    }
    
    setIsDeleting(true);
    
    try {
      // Call backend to delete account
      await axios.delete('/users/me');
      
      // Clear all local data
      localStorage.clear();
      delete axios.defaults.headers.common['Authorization'];
      
      toast.success('Your account has been deleted successfully');
      
      // Redirect to landing page
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleVisibilitySettingsChange = (setting, value) => {
    setProfileVisibilitySettings(prev => ({
      ...prev,
      [setting]: value
    }));
    // Auto-save to backend
    saveVisibilitySettings({ ...profileVisibilitySettings, [setting]: value });
  };

  const handleGenderPreferenceChange = (preference) => {
    setGenderPreference(preference);
    setShowGenderDropdown(false);
    // Auto-save to backend
    saveGenderPreference(preference);
  };

  const saveVisibilitySettings = async (settings) => {
    try {
      await axios.put('/users/me/visibility-settings', settings);
      toast.success('Visibility settings updated', { duration: 2000 });
    } catch (error) {
      console.error('Error saving visibility settings:', error);
      toast.error('Failed to save visibility settings');
    }
  };

  const saveGenderPreference = async (preference) => {
    try {
      await axios.put('/users/me/gender-preference', { preference });
      toast.success('Gender preference updated', { duration: 2000 });
    } catch (error) {
      console.error('Error saving gender preference:', error);
      toast.error('Failed to save gender preference');
    }
  };

  const saveNotificationSettings = async (settings) => {
    try {
      await axios.put('/users/me/notification-settings', settings);
      toast.success('Notification settings updated', { duration: 2000 });
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error('Failed to save notification settings');
    }
  };

  const handleSaveMobileNumber = async () => {
    if (!mobileNumber.trim()) {
      toast.error('Mobile number cannot be empty');
      return;
    }
    setIsSavingMobile(true);
    try {
      await axios.put('/users/me', { mobile_number: mobileNumber.trim() });
      setSavedMobileNumber(mobileNumber.trim());
      toast.success('Mobile number updated successfully');
      setIsEditingMobile(false);
    } catch (error) {
      console.error('Error updating mobile number:', error);
      toast.error('Failed to update mobile number');
    } finally {
      setIsSavingMobile(false);
    }
  };

  return (
    <div className="min-h-screen pt-6 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-emerald-800 dark:text-emerald-100 mb-4">
            Settings
          </h1>
          <p className="text-lg text-emerald-600 dark:text-emerald-300 max-w-2xl mx-auto">
            Customize your eternitysingles® experience and manage your preferences.
          </p>
        </div>

        <div className="space-y-6">
          {/* AI Counselor Quick Access */}
          <div 
            className="card cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-emerald-300 dark:hover:border-emerald-600"
            onClick={() => navigate('/counselor')}
            data-testid="ai-counselor-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-emerald-800 dark:text-emerald-100">
                    AI Wellness Counsellor
                  </h2>
                  <p className="text-sm text-emerald-600 dark:text-emerald-300">
                    Talk to Eternity for mental wellness & dating support
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-medium rounded-full">
                  NEW
                </span>
                <ChevronDown className="w-5 h-5 text-emerald-400 -rotate-90" />
              </div>
            </div>
          </div>

          {/* Admin Dashboard - Only visible to admins */}
          {user?.is_admin && (
            <div 
              className="card cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-amber-300 dark:hover:border-amber-600"
              onClick={() => navigate('/admin')}
              data-testid="admin-dashboard-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-amber-800 dark:text-amber-100">
                      Admin Dashboard
                    </h2>
                    <p className="text-sm text-amber-600 dark:text-amber-300">
                      Manage users, view stats, and moderate content
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium rounded-full">
                    ADMIN
                  </span>
                  <ChevronDown className="w-5 h-5 text-amber-400 -rotate-90" />
                </div>
              </div>
            </div>
          )}

          {/* Theme Settings */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 dark:from-emerald-400 dark:to-green-500 rounded-lg flex items-center justify-center">
                <Palette className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100">
                Appearance
              </h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center space-x-3">
                  {isDark ? (
                    <Moon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <Sun className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                      Theme Mode
                    </h3>
                    <p className="text-xs text-emerald-600 dark:text-emerald-300">
                      Choose between light and dark mode
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={toggleTheme}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isDark 
                      ? 'bg-emerald-600 dark:bg-emerald-500' 
                      : 'bg-gray-200 dark:bg-slate-600'
                  }`}
                  data-testid="theme-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isDark ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    !isDark 
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-slate-800' 
                      : 'border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-700'
                  }`}
                  onClick={() => !isDark || toggleTheme()}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <Sun className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                      Light Mode
                    </span>
                  </div>
                  <div className="bg-white dark:bg-slate-900 rounded p-2 border border-gray-200 dark:border-slate-600">
                    <div className="h-2 bg-emerald-500 rounded mb-1"></div>
                    <div className="h-1 bg-gray-300 dark:bg-slate-600 rounded mb-1"></div>
                    <div className="h-1 bg-gray-300 dark:bg-slate-600 rounded w-2/3"></div>
                  </div>
                </div>

                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isDark 
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-slate-800' 
                      : 'border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-700'
                  }`}
                  onClick={() => isDark || toggleTheme()}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <Moon className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                      Dark Mode
                    </span>
                  </div>
                  <div className="bg-slate-900 rounded p-2 border border-slate-700">
                    <div className="h-2 bg-emerald-500 rounded mb-1"></div>
                    <div className="h-1 bg-slate-600 rounded mb-1"></div>
                    <div className="h-1 bg-slate-600 rounded w-2/3"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Settings */}
          <div className="card">
            <button
              onClick={() => toggleSection('account')}
              className="w-full flex items-center justify-between mb-6 group"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100">
                  Account
                </h2>
              </div>
              <ChevronDown className={`w-5 h-5 text-emerald-600 dark:text-emerald-400 transition-transform duration-200 ${expandedSections.account ? 'rotate-180' : ''}`} />
            </button>

            {expandedSections.account && (
              <div className="space-y-4 animate-fade-in-up">
              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    Account Email
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    {user?.email || 'Not available'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    Full Name
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    {user?.full_name || 'Not available'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    Username
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    {user?.username || 'Not available'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div className="flex-1 mr-4">
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Mobile Number
                  </h3>
                  {isEditingMobile ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="tel"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value)}
                        placeholder="Enter mobile number"
                        className="flex-1 px-3 py-2 bg-white dark:bg-slate-700 border border-emerald-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm text-emerald-800 dark:text-emerald-100"
                        data-testid="mobile-number-input"
                        disabled={isSavingMobile}
                      />
                      <button
                        onClick={handleSaveMobileNumber}
                        disabled={isSavingMobile}
                        className="px-3 py-2 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                        data-testid="save-mobile-btn"
                      >
                        {isSavingMobile ? '...' : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => { setIsEditingMobile(false); setMobileNumber(savedMobileNumber); }}
                        className="px-3 py-2 bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors"
                        data-testid="cancel-mobile-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-emerald-600 dark:text-emerald-300">
                        {mobileNumber || 'Not set'}
                      </p>
                    </div>
                  )}
                </div>
                {!isEditingMobile && (
                  <button
                    onClick={() => setIsEditingMobile(true)}
                    className="px-3 py-1 bg-emerald-100 dark:bg-slate-700 text-emerald-700 dark:text-emerald-300 text-xs rounded hover:bg-emerald-200 dark:hover:bg-slate-600 transition-colors"
                    data-testid="edit-mobile-btn"
                  >
                    Edit
                  </button>
                )}
              </div>
              </div>
            )}
          </div>

          {/* Security (Change Password) */}
          <div className="card">
            <button
              onClick={() => toggleSection('security')}
              className="w-full flex items-center justify-between mb-6 group"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Lock className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100">
                  Security
                </h2>
              </div>
              <ChevronDown className={`w-5 h-5 text-emerald-600 dark:text-emerald-400 transition-transform duration-200 ${expandedSections.security ? 'rotate-180' : ''}`} />
            </button>

            {expandedSections.security && (
              <form onSubmit={handleChangePassword} className="space-y-6 animate-fade-in-up">
              <div className="space-y-4">
                {/* Current Password */}
                <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex-1 mr-4">
                    <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100 mb-1">
                      Current Password
                    </h3>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-emerald-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm text-emerald-800 dark:text-emerald-100 pr-10"
                        disabled={isChangingPassword}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* New Password */}
                <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100 mb-1">
                      New Password
                    </h3>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min 6 characters"
                        className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-emerald-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm text-emerald-800 dark:text-emerald-100 pr-10"
                        disabled={isChangingPassword}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {newPassword && newPassword.length < 6 && (
                      <p className="text-xs text-red-500 mt-1">Must be at least 6 characters</p>
                    )}
                  </div>
                </div>

                {/* Confirm New Password */}
                <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100 mb-1">
                      Confirm Password
                    </h3>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter new password"
                        className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-emerald-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm text-emerald-800 dark:text-emerald-100 pr-10"
                        disabled={isChangingPassword}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword || newPassword.length < 6}
                  className="w-full px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-semibold rounded-lg hover:from-emerald-700 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isChangingPassword ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Changing Password...</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      <span>Update Password</span>
                    </>
                  )}
                </button>
              </div>
              </form>
            )}
          </div>

          {/* Notifications Settings */}
          <div className="card">
            <button
              onClick={() => toggleSection('notifications')}
              className="w-full flex items-center justify-between mb-6 group"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <Bell className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100">
                  Notifications
                </h2>
              </div>
              <ChevronDown className={`w-5 h-5 text-emerald-600 dark:text-emerald-400 transition-transform duration-200 ${expandedSections.notifications ? 'rotate-180' : ''}`} />
            </button>

            {expandedSections.notifications && (
              <div className="space-y-4 animate-fade-in-up">
              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    Match Notifications
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    Get notified when you have new matches
                  </p>
                </div>
                <button
                  onClick={() => {
                    const newValue = !matchNotifications;
                    setMatchNotifications(newValue);
                    saveNotificationSettings({
                      matches: newValue,
                      messages: messageNotifications,
                      email: emailNotifications,
                      push: pushNotifications
                    });
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    matchNotifications 
                      ? 'bg-emerald-600 dark:bg-emerald-500' 
                      : 'bg-gray-200 dark:bg-slate-600'
                  }`}
                  data-testid="match-notifications-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      matchNotifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    Message Notifications
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    Get notified when you receive messages
                  </p>
                </div>
                <button
                  onClick={() => {
                    const newValue = !messageNotifications;
                    setMessageNotifications(newValue);
                    saveNotificationSettings({
                      matches: matchNotifications,
                      messages: newValue,
                      email: emailNotifications,
                      push: pushNotifications
                    });
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    messageNotifications 
                      ? 'bg-emerald-600 dark:bg-emerald-500' 
                      : 'bg-gray-200 dark:bg-slate-600'
                  }`}
                  data-testid="message-notifications-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      messageNotifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    Email Notifications
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    Receive notifications via email
                  </p>
                </div>
                <button
                  onClick={() => {
                    const newValue = !emailNotifications;
                    setEmailNotifications(newValue);
                    saveNotificationSettings({
                      matches: matchNotifications,
                      messages: messageNotifications,
                      email: newValue,
                      push: pushNotifications
                    });
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    emailNotifications 
                      ? 'bg-emerald-600 dark:bg-emerald-500' 
                      : 'bg-gray-200 dark:bg-slate-600'
                  }`}
                  data-testid="email-notifications-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      emailNotifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    Push Notifications
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    Allow browser push notifications
                  </p>
                </div>
                <button
                  onClick={() => {
                    const newValue = !pushNotifications;
                    setPushNotifications(newValue);
                    saveNotificationSettings({
                      matches: matchNotifications,
                      messages: messageNotifications,
                      email: emailNotifications,
                      push: newValue
                    });
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    pushNotifications 
                      ? 'bg-emerald-600 dark:bg-emerald-500' 
                      : 'bg-gray-200 dark:bg-slate-600'
                  }`}
                  data-testid="push-notifications-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      pushNotifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              </div>
            )}
          </div>

          {/* Gender Preferences */}
          <div className="card">
            <button
              onClick={() => toggleSection('genderPreferences')}
              className="w-full flex items-center justify-between mb-6 group"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100">
                  Gender Preferences
                </h2>
              </div>
              <ChevronDown className={`w-5 h-5 text-emerald-600 dark:text-emerald-400 transition-transform duration-200 ${expandedSections.genderPreferences ? 'rotate-180' : ''}`} />
            </button>

            {expandedSections.genderPreferences && (
              <div className="space-y-6 animate-fade-in-up">
              {/* Gender Preference Dropdown */}
              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    I am
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    Choose your dating preference
                  </p>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowGenderDropdown(!showGenderDropdown)}
                    className="flex items-center space-x-2 px-3 py-1 bg-emerald-100 dark:bg-slate-700 rounded-lg text-xs text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-slate-600 transition-colors"
                    data-testid="gender-preference-dropdown-toggle"
                  >
                    <span>{genderPreference}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  
                  {showGenderDropdown && (
                    <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-slate-600 rounded-lg shadow-lg z-10">
                      {[
                        'Men Looking for Women',
                        'Women Looking for Men',
                        'Men Looking for Men',
                        'Women Looking for Women'
                      ].map((option) => (
                        <button
                          key={option}
                          onClick={() => handleGenderPreferenceChange(option)}
                          className="w-full text-left px-3 py-2 text-xs text-emerald-800 dark:text-emerald-100 hover:bg-emerald-50 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg transition-colors"
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Hide your profile from certain genders */}
              <div className="pt-4 border-t border-emerald-200 dark:border-slate-600">
                <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-3">
                  Hide my profile from:
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div>
                      <span className="text-sm font-medium text-emerald-800 dark:text-emerald-100">Men</span>
                      <p className="text-xs text-emerald-600 dark:text-emerald-300">Men won't see your profile</p>
                    </div>
                    <button
                      onClick={() => handleVisibilitySettingsChange('hideFromMen', !profileVisibilitySettings.hideFromMen)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        profileVisibilitySettings.hideFromMen 
                          ? 'bg-red-600 dark:bg-red-500' 
                          : 'bg-gray-200 dark:bg-slate-600'
                      }`}
                      data-testid="hide-from-men-toggle"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          profileVisibilitySettings.hideFromMen ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div>
                      <span className="text-sm font-medium text-emerald-800 dark:text-emerald-100">Women</span>
                      <p className="text-xs text-emerald-600 dark:text-emerald-300">Women won't see your profile</p>
                    </div>
                    <button
                      onClick={() => handleVisibilitySettingsChange('hideFromWomen', !profileVisibilitySettings.hideFromWomen)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        profileVisibilitySettings.hideFromWomen 
                          ? 'bg-red-600 dark:bg-red-500' 
                          : 'bg-gray-200 dark:bg-slate-600'
                      }`}
                      data-testid="hide-from-women-toggle"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          profileVisibilitySettings.hideFromWomen ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
              </div>
            )}
          </div>

          {/* Privacy & Safety */}
          <div className="card">
            <button
              onClick={() => toggleSection('privacy')}
              className="w-full flex items-center justify-between mb-6 group"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100">
                  Privacy & Safety
                </h2>
              </div>
              <ChevronDown className={`w-5 h-5 text-emerald-600 dark:text-emerald-400 transition-transform duration-200 ${expandedSections.privacy ? 'rotate-180' : ''}`} />
            </button>

            {expandedSections.privacy && (
              <div className="space-y-4 animate-fade-in-up">
              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    Profile Visibility
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    Control who can see your profile
                  </p>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowPrivacyDropdown(!showPrivacyDropdown)}
                    className="flex items-center space-x-2 px-3 py-1 bg-emerald-100 dark:bg-slate-700 rounded-lg text-xs text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-slate-600 transition-colors"
                    data-testid="privacy-dropdown-toggle"
                  >
                    <span>{profileVisibility}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  
                  {showPrivacyDropdown && (
                    <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-slate-600 rounded-lg shadow-lg z-10">
                      {['Public', 'Friends Only', 'Private'].map((option) => (
                        <button
                          key={option}
                          onClick={() => {
                            setProfileVisibility(option);
                            setShowPrivacyDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 text-xs text-emerald-800 dark:text-emerald-100 hover:bg-emerald-50 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg transition-colors"
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    Data & Privacy
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300">
                    Download your data or delete your account
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={handleDownloadData}
                    className="px-3 py-1 bg-emerald-100 dark:bg-slate-700 text-emerald-700 dark:text-emerald-300 text-xs rounded hover:bg-emerald-200 dark:hover:bg-slate-600 transition-colors"
                    data-testid="download-data-btn"
                  >
                    Download
                  </button>
                  <button 
                    onClick={() => setShowDeleteModal(true)}
                    className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                    data-testid="delete-account-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
              </div>
            )}
          </div>

          {/* Legal & Policies Section */}
          <div className="card">
            <button
              onClick={() => toggleSection('legal')}
              className="w-full flex items-center justify-between cursor-pointer"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 dark:from-blue-400 dark:to-indigo-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100">
                  Legal & Policies
                </h2>
              </div>
              <ChevronDown className={`w-5 h-5 text-emerald-600 dark:text-emerald-400 transition-transform duration-200 ${expandedSections.legal ? 'rotate-180' : ''}`} />
            </button>
            {expandedSections.legal && (
              <div className="space-y-3 mt-6" onClick={(e) => e.stopPropagation()}>
                {/* Terms and Conditions */}
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const ok = downloadPolicy('Terms and Conditions', TERMS_AND_CONDITIONS);
                      ok ? toast.success('Terms and Conditions downloaded') : toast.error('Download failed');
                    }}
                    className="flex-1 flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 hover:bg-emerald-100 dark:hover:bg-slate-700 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      <div className="text-left">
                        <p className="font-medium text-emerald-800 dark:text-emerald-100">Terms and Conditions</p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-300">Tap to download</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(TERMS_AND_CONDITIONS, 'Terms and Conditions').then((result) => {
                        if (result.success) {
                          toast.success(result.message);
                        } else {
                          toast.error(result.message);
                        }
                      });
                    }}
                    className="p-4 bg-emerald-50 dark:bg-slate-800 hover:bg-emerald-100 dark:hover:bg-slate-700 rounded-lg transition-colors group"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-5 h-5 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
                  </button>
                </div>

                {/* Privacy Policy */}
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const ok = downloadPolicy('Privacy Policy', PRIVACY_POLICY);
                      ok ? toast.success('Privacy Policy downloaded') : toast.error('Download failed');
                    }}
                    className="flex-1 flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 hover:bg-emerald-100 dark:hover:bg-slate-700 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      <div className="text-left">
                        <p className="font-medium text-emerald-800 dark:text-emerald-100">Privacy Policy</p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-300">Tap to download</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(PRIVACY_POLICY, 'Privacy Policy').then((result) => {
                        if (result.success) {
                          toast.success(result.message);
                        } else {
                          toast.error(result.message);
                        }
                      });
                    }}
                    className="p-4 bg-emerald-50 dark:bg-slate-800 hover:bg-emerald-100 dark:hover:bg-slate-700 rounded-lg transition-colors group"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-5 h-5 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
                  </button>
                </div>

                {/* Cookies Policy */}
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const ok = downloadPolicy('Cookies Policy', COOKIES_POLICY);
                      ok ? toast.success('Cookies Policy downloaded') : toast.error('Download failed');
                    }}
                    className="flex-1 flex items-center justify-between p-4 bg-emerald-50 dark:bg-slate-800 hover:bg-emerald-100 dark:hover:bg-slate-700 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Info className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      <div className="text-left">
                        <p className="font-medium text-emerald-800 dark:text-emerald-100">Cookies Policy</p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-300">Tap to download</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(COOKIES_POLICY, 'Cookies Policy').then((result) => {
                        if (result.success) {
                          toast.success(result.message);
                        } else {
                          toast.error(result.message);
                        }
                      });
                    }}
                    className="p-4 bg-emerald-50 dark:bg-slate-800 hover:bg-emerald-100 dark:hover:bg-slate-700 rounded-lg transition-colors group"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-5 h-5 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
                  </button>
                </div>

                {/* End of policies */}

                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Note:</strong> Tap to download files, or use the copy button (<Copy className="w-3 h-3 inline" />) to copy text to clipboard.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Help & FAQ Section */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 dark:from-blue-400 dark:to-indigo-500 rounded-lg flex items-center justify-center">
                <Info className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100">
                Help & Support
              </h2>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/faq')}
                className="w-full p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg hover:bg-emerald-100 dark:hover:bg-slate-700 transition-colors text-left"
              >
                <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100 mb-1">
                  Frequently Asked Questions
                </h3>
                <p className="text-xs text-emerald-600 dark:text-emerald-300">
                  Find answers to common questions about using the app
                </p>
              </button>
              
              <a
                href="mailto:info@eternitysingles.com"
                className="w-full p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg hover:bg-emerald-100 dark:hover:bg-slate-700 transition-colors text-left block"
              >
                <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100 mb-1">
                  Contact Support
                </h3>
                <p className="text-xs text-emerald-600 dark:text-emerald-300">
                  info@eternitysingles.com
                </p>
              </a>
            </div>
          </div>

          {/* About Section - Always Visible */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 dark:from-emerald-400 dark:to-green-500 rounded-lg flex items-center justify-center">
                <Info className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100">
                About
              </h2>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => setShowAboutModal(true)}
                className="w-full p-4 bg-emerald-50 dark:bg-slate-800 rounded-lg text-center hover:bg-emerald-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                data-testid="about-eternitysingles-btn"
              >
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Heart className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-100">
                    eternitysingles<sup>®</sup>
                  </h3>
                </div>
                <p className="text-xs text-emerald-600 dark:text-emerald-300 mb-2">
                  Mental Health & Wellbeing Dating App
                </p>
                <p className="text-xs text-emerald-500 dark:text-emerald-400 mb-2">
                  Version 1.0.0
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-300 underline">
                  Tap to learn more about us
                </p>
              </button>
            </div>
          </div>
        </div>

      {/* About eternitysingles Modal */}
      {showAboutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-green-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Heart className="w-8 h-8" />
                  <div>
                    <h2 className="text-xl font-bold">{ABOUT_ETERNITYSINGLES.title}</h2>
                    <p className="text-emerald-100 text-sm">{ABOUT_ETERNITYSINGLES.tagline}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAboutModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[55vh]">
              {/* Intro */}
              <p className="text-emerald-700 dark:text-emerald-300 leading-relaxed mb-6">
                {ABOUT_ETERNITYSINGLES.intro}
              </p>
              
              {/* Sections */}
              {ABOUT_ETERNITYSINGLES.sections.map((section, index) => (
                <div key={index} className="mb-6">
                  <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                    {section.title}
                  </h3>
                  
                  {section.content && (
                    <p className="text-emerald-700 dark:text-emerald-300 leading-relaxed">{section.content}</p>
                  )}
                  
                  {section.items && (
                    <ul className="space-y-3">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start space-x-3 p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg">
                          <span className="text-emerald-500 font-bold">•</span>
                          <div>
                            <span className="font-medium text-emerald-800 dark:text-emerald-100">{item.label}:</span>
                            <span className="text-emerald-600 dark:text-emerald-300 ml-1">{item.desc}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.values && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {section.values.map((value, i) => (
                        <div key={i} className="flex items-center space-x-3 p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg">
                          <span className="text-2xl">{value.emoji}</span>
                          <div>
                            <p className="font-medium text-emerald-800 dark:text-emerald-100">{value.label}</p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-400">{value.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-slate-900 p-4 border-t border-gray-200 dark:border-slate-700">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-center sm:text-left">
                  <a
                    href="mailto:info@eternitysingles.com"
                    className="text-emerald-600 dark:text-emerald-400 hover:underline text-sm"
                  >
                    info@eternitysingles.com
                  </a>
                  <p className="text-xs text-emerald-500 dark:text-emerald-400 mt-1">{ABOUT_ETERNITYSINGLES.footer}</p>
                </div>
                <button
                  onClick={() => setShowAboutModal(false)}
                  className="btn-primary px-6"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Account</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
              </div>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                  setIsDeleting(false);
                }}
                className="ml-auto p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">Warning: This will permanently:</h4>
                <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                  <li>• Delete your profile and all personal data</li>
                  <li>• Remove all your matches and conversations</li>
                  <li>• Cancel any subscriptions</li>
                  <li>• Make your account unrecoverable</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type "delete my account" to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="delete my account"
                  disabled={isDeleting}
                />
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                    setIsDeleting(false);
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || deleteConfirmText.toLowerCase() !== 'delete my account'}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="confirm-delete-btn"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Settings;